import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import FileUploader from "@/components/FileUploader";
import { PlusIcon, XIcon, TrashIcon, ProhibitIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/**
 * §4.6 / §14 — Formulario general de registro de gasto + lista con filtros.
 * Postea asiento y movimiento de tesorería; retenciones generan constancia.
 */
const METODOS = [
  { v: "efectivo", l: "Efectivo" }, { v: "transferencia", l: "Transferencia" }, { v: "cheque", l: "Cheque" },
  { v: "deposito", l: "Depósito" }, { v: "tarjeta", l: "Tarjeta débito" }, { v: "tarjeta_credito", l: "Tarjeta de crédito" },
];
const today = () => new Date().toISOString().slice(0, 10);

export default function GeneralExpensesTab() {
  const [items, setItems] = useState([]);
  const [partidas, setPartidas] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [reps, setReps] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ desde: "", hasta: "", partida_codigo: "", proveedor_id: "", region: "", estado: "registrado", q: "" });

  const load = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    try {
      const { data } = await api.get("/accounting/general-expenses", { params });
      setItems(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => {
    (async () => {
      const [p, s, b, r] = await Promise.all([
        api.get("/accounting/expense-accounts").catch(() => ({ data: [] })),
        api.get("/suppliers").catch(() => ({ data: [] })),
        api.get("/treasury/accounts").catch(() => ({ data: [] })),
        api.get("/representatives").catch(() => ({ data: [] })),
      ]);
      setPartidas(p.data || []); setSuppliers(s.data || []); setBanks(b.data || []); setReps(r.data || []);
    })();
  }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.desde, f.hasta, f.partida_codigo, f.proveedor_id, f.estado]);

  const voidIt = async (g) => {
    if (!window.confirm(`¿Anular gasto ${g.numero}? Se revierten asiento y tesorería.`)) return;
    try { await api.post(`/accounting/general-expenses/${g.id}/void`); toast.success("Gasto anulado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const total = items.filter((x) => x.estado !== "anulado").reduce((s, x) => s + (x.total || 0), 0);
  const inp = "bg-app border border-border rounded-md px-2 py-1.5 text-xs";
  return (
    <div className="space-y-4" data-testid="general-expenses-tab">
      <div className="bg-card border border-border/60 rounded-lg p-3 flex flex-wrap gap-2 items-end">
        <Fl l="Desde"><input type="date" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} className={inp} /></Fl>
        <Fl l="Hasta"><input type="date" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} className={inp} /></Fl>
        <Fl l="Partida">
          <select value={f.partida_codigo} onChange={(e) => setF({ ...f, partida_codigo: e.target.value })} className={`${inp} w-44`}>
            <option value="">Todas</option>
            {partidas.map((p) => <option key={p.codigo} value={p.codigo}>{p.codigo} · {p.nombre}</option>)}
          </select>
        </Fl>
        <Fl l="Proveedor">
          <select value={f.proveedor_id} onChange={(e) => setF({ ...f, proveedor_id: e.target.value })} className={`${inp} w-40`}>
            <option value="">Todos</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
          </select>
        </Fl>
        <Fl l="Estado">
          <select value={f.estado} onChange={(e) => setF({ ...f, estado: e.target.value })} className={inp}>
            <option value="">Todos</option><option value="registrado">Registrado</option><option value="anulado">Anulado</option>
          </select>
        </Fl>
        <Fl l="Región"><input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} onBlur={load} className={`${inp} w-28`} /></Fl>
        <Fl l="Buscar"><input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="N° doc, descripción…" className={`${inp} w-40`} /></Fl>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-text-muted">{items.length} gastos · <b className="text-agro-green">{fmtL(total)}</b></span>
          <button onClick={() => setOpen(true)} data-testid="new-general-expense" className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" /> Registrar gasto
          </button>
        </div>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">N°</th><th className="p-3">Fecha</th><th className="p-3">Documento</th><th className="p-3">Proveedor</th>
              <th className="p-3">Partida</th><th className="p-3">Descripción</th><th className="p-3 text-right">Total</th>
              <th className="p-3 text-right">Retenido</th><th className="p-3">Pago</th><th className="p-3">Estado</th><th className="p-3 pr-5"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan="11" className="p-10 text-center text-text-muted">Sin gastos registrados</td></tr>
              : items.map((g) => (
                <tr key={g.id} className="border-t border-border/40" data-testid={`gasto-row-${g.numero}`}>
                  <td className="p-3 pl-5 font-mono text-xs text-agro-green">{g.numero}</td>
                  <td className="p-3 text-xs">{fmtDate(g.fecha)}</td>
                  <td className="p-3 text-xs font-mono">{g.numero_documento}</td>
                  <td className="p-3 text-sm">{g.proveedor_nombre || "—"}</td>
                  <td className="p-3 text-xs">{g.partida_codigo} · {g.partida_nombre}</td>
                  <td className="p-3 text-xs text-text-secondary max-w-[220px] truncate">{g.descripcion}{g.distribucion?.length ? ` · distribuido (${g.distribucion.length})` : ""}</td>
                  <td className="p-3 text-right text-sm">{fmtL(g.total)}</td>
                  <td className="p-3 text-right text-xs">{fmtL((g.retencion_isr || 0) + (g.retencion_isv || 0))}</td>
                  <td className="p-3 text-xs">{g.metodo}{g.cuenta_banco_codigo ? ` · ${g.cuenta_banco_codigo}` : ""}</td>
                  <td className="p-3 text-[10px] uppercase">{g.estado}</td>
                  <td className="p-3 pr-5 text-right">
                    {(g.respaldos || []).slice(0, 1).map((r) => <a key={r.url} href={r.url} target="_blank" rel="noreferrer" className="text-[10px] text-agro-yellow underline mr-2">Respaldo</a>)}
                    {g.estado !== "anulado" && <button onClick={() => voidIt(g)} title="Anular" aria-label="Anular" className="p-1 text-text-muted hover:text-red-400"><ProhibitIcon size={15} /></button>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {open && <ExpenseForm partidas={partidas} suppliers={suppliers} banks={banks} reps={reps}
        onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function ExpenseForm({ partidas, suppliers, banks, reps, onClose, onSaved }) {
  const [form, setForm] = useState({
    numero_documento: "", fecha: today(), proveedor_id: "", partida_codigo: "", descripcion: "",
    monto: "", isv: "", retencion_isr: "", retencion_isv: "", metodo: "efectivo", cuenta_banco_id: "",
    referencia: "", region: "", rtc_id: "",
  });
  const [respaldos, setRespaldos] = useState([]);
  const [distOn, setDistOn] = useState(false);
  const [dist, setDist] = useState([{ partida_codigo: "", proveedor_id: "", linea: "", porcentaje: 100 }]);
  const [saving, setSaving] = useState(false);
  const partida = partidas.find((p) => p.codigo === form.partida_codigo);
  const set = (patch) => setForm((x) => ({ ...x, ...patch }));

  const pickPartida = (cod) => {
    const p = partidas.find((x) => x.codigo === cod);
    const patch = { partida_codigo: cod };
    if (p && p.proveedor_default_id && !form.proveedor_id) patch.proveedor_id = p.proveedor_default_id;
    if (p && p.region_default && !form.region) patch.region = p.region_default;
    set(patch);
  };
  const cuentas = useMemo(() => {
    if (form.metodo === "efectivo") return banks.filter((b) => b.tipo === "caja");
    if (form.metodo === "tarjeta_credito") return banks.filter((b) => b.tipo === "tarjeta");
    return banks.filter((b) => b.tipo !== "caja");
  }, [banks, form.metodo]);
  const total = (Number(form.monto) || 0) + (Number(form.isv) || 0);
  const ret = (Number(form.retencion_isr) || 0) + (Number(form.retencion_isv) || 0);
  const sumPct = dist.reduce((s, d) => s + (Number(d.porcentaje) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.partida_codigo) return toast.error("Selecciona la partida");
    if (form.metodo !== "tarjeta_credito" && !form.cuenta_banco_id) return toast.error("Selecciona la cuenta de tesorería");
    if (distOn && Math.abs(sumPct - 100) > 0.01) return toast.error(`La distribución debe sumar 100% (actual ${sumPct}%)`);
    const rtc = reps.find((r) => r.id === form.rtc_id);
    setSaving(true);
    try {
      await api.post("/accounting/general-expenses", {
        ...form, monto: Number(form.monto), isv: Number(form.isv) || 0,
        retencion_isr: Number(form.retencion_isr) || 0, retencion_isv: Number(form.retencion_isv) || 0,
        proveedor_id: form.proveedor_id || null, cuenta_banco_id: form.cuenta_banco_id || null,
        rtc_id: form.rtc_id || null, rtc_nombre: rtc ? rtc.nombre : "",
        respaldos: respaldos.map((r) => ({ url: r.url, original_filename: r.original_filename, id: r.id })),
        distribucion: distOn ? dist.map((d) => ({
          partida_codigo: d.partida_codigo || null, proveedor_id: d.proveedor_id || null,
          linea: d.linea || "", porcentaje: Number(d.porcentaje) || 0,
        })) : [],
      });
      toast.success("Gasto registrado · asiento y tesorería generados");
      onSaved();
    } catch (er) { toast.error(apiErr(er)); }
    finally { setSaving(false); }
  };

  const inp = "w-full bg-app border border-border rounded-md px-3 py-2 text-sm";
  const gm = useModalForm({ value: { form, distOn, dist, r: respaldos.length }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...gm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="general-expense-modal">
        <div className="p-5 border-b border-border flex justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Registro de gasto</div>
            <h3 className="font-heading text-lg font-semibold">Nuevo gasto</h3>
          </div>
          <button aria-label="Cerrar" onClick={gm.cerrar} className="text-text-muted"><XIcon size={20} /></button>
        </div>
        <form onSubmit={gm.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Fl l="N° documento / factura *"><input required value={form.numero_documento} onChange={(e) => set({ numero_documento: e.target.value })} className={inp} data-testid="ge-doc" /></Fl>
            <Fl l="Fecha *"><input type="date" required value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} className={inp} /></Fl>
            <Fl l="Proveedor">
              <select value={form.proveedor_id} onChange={(e) => set({ proveedor_id: e.target.value })} className={inp}>
                <option value="">— Sin proveedor —</option>
                {suppliers.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
            </Fl>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Fl l="Partida *">
                <select required value={form.partida_codigo} onChange={(e) => pickPartida(e.target.value)} className={inp} data-testid="ge-partida">
                  <option value="">— Selecciona —</option>
                  {partidas.map((p) => <option key={p.codigo} value={p.codigo}>{p.codigo} · {p.nombre}</option>)}
                </select>
              </Fl>
            </div>
            <div className="text-[11px] text-text-muted pt-5">
              {partida ? <>Cuenta: <b>{partida.cuenta_contable || "6106"}</b> · Tipo: {partida.tipo} · {partida.deducible ? "Deducible" : "No deducible"}</> : "Jala cuenta, tipo y deducibilidad de la partida"}
            </div>
          </div>
          <Fl l="Descripción"><input value={form.descripcion} onChange={(e) => set({ descripcion: e.target.value })} className={inp} /></Fl>
          <div className="grid grid-cols-4 gap-3">
            <Fl l="Monto (sin ISV) *"><input type="number" step="0.01" required value={form.monto} onChange={(e) => set({ monto: e.target.value })} className={inp} data-testid="ge-monto" /></Fl>
            <Fl l="ISV"><input type="number" step="0.01" value={form.isv} onChange={(e) => set({ isv: e.target.value })} className={inp} /></Fl>
            <Fl l="ISR retenido"><input type="number" step="0.01" value={form.retencion_isr} onChange={(e) => set({ retencion_isr: e.target.value })} className={inp} /></Fl>
            <Fl l="ISV retenido"><input type="number" step="0.01" value={form.retencion_isv} onChange={(e) => set({ retencion_isv: e.target.value })} className={inp} /></Fl>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Fl l="Método de pago *">
              <select value={form.metodo} onChange={(e) => set({ metodo: e.target.value, cuenta_banco_id: "" })} className={inp}>
                {METODOS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>
            </Fl>
            <Fl l={form.metodo === "tarjeta_credito" ? "Tarjeta (opcional)" : "Cuenta de tesorería *"}>
              <select value={form.cuenta_banco_id} onChange={(e) => set({ cuenta_banco_id: e.target.value })} className={inp} data-testid="ge-cuenta">
                <option value="">{form.metodo === "tarjeta_credito" ? "Tarjetas de crédito por pagar" : "— Selecciona —"}</option>
                {cuentas.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre} ({fmtL(b.saldo_actual)})</option>)}
              </select>
            </Fl>
            <Fl l="Referencia pago"><input value={form.referencia} onChange={(e) => set({ referencia: e.target.value })} className={inp} /></Fl>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fl l="Región"><input value={form.region} onChange={(e) => set({ region: e.target.value })} className={inp} /></Fl>
            <Fl l="RTC">
              <select value={form.rtc_id} onChange={(e) => set({ rtc_id: e.target.value })} className={inp}>
                <option value="">— Ninguno —</option>
                {reps.map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
              </select>
            </Fl>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5">Respaldo</div>
            <FileUploader folder="documents" files={respaldos} onUploaded={(file) => setRespaldos((x) => [...x, file])}
              onRemove={(file) => setRespaldos((x) => x.filter((y) => y.url !== file.url))} label="Adjuntar respaldo" />
          </div>
          <div className="border border-border/60 rounded-md p-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={distOn} onChange={(e) => setDistOn(e.target.checked)} className="accent-agro-green" />
              Distribuir el gasto por % (partida / proveedor / línea)
            </label>
            {distOn && (
              <div className="mt-3 space-y-2">
                {dist.map((d, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <select value={d.partida_codigo} onChange={(e) => setDist(dist.map((x, j) => j === i ? { ...x, partida_codigo: e.target.value } : x))} className="col-span-4 bg-app border border-border rounded px-2 py-1 text-xs">
                      <option value="">— partida —</option>
                      {partidas.map((p) => <option key={p.codigo} value={p.codigo}>{p.codigo} · {p.nombre}</option>)}
                    </select>
                    <select value={d.proveedor_id} onChange={(e) => setDist(dist.map((x, j) => j === i ? { ...x, proveedor_id: e.target.value } : x))} className="col-span-3 bg-app border border-border rounded px-2 py-1 text-xs">
                      <option value="">— proveedor —</option>
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                    </select>
                    <input value={d.linea} placeholder="Línea" onChange={(e) => setDist(dist.map((x, j) => j === i ? { ...x, linea: e.target.value } : x))} className="col-span-2 bg-app border border-border rounded px-2 py-1 text-xs" />
                    <input type="number" value={d.porcentaje} onChange={(e) => setDist(dist.map((x, j) => j === i ? { ...x, porcentaje: e.target.value } : x))} className="col-span-2 bg-app border border-border rounded px-2 py-1 text-xs text-right" />
                    <button aria-label="Eliminar" type="button" onClick={() => setDist(dist.filter((_x, j) => j !== i))} className="text-text-muted hover:text-red-400"><TrashIcon size={14} /></button>
                  </div>
                ))}
                <div className="flex justify-between text-xs">
                  <button type="button" onClick={() => setDist([...dist, { partida_codigo: "", proveedor_id: "", linea: "", porcentaje: 0 }])} className="text-agro-green hover:underline">+ Agregar línea</button>
                  <span className={Math.abs(sumPct - 100) > 0.01 ? "text-red-400" : "text-agro-green"}>Suma: {sumPct}%</span>
                </div>
              </div>
            )}
          </div>
          <div className="bg-app border border-border rounded-md p-3 text-sm flex justify-between">
            <span>Total {fmtL(total)} · Retenciones {fmtL(ret)}</span>
            <span className="font-semibold text-agro-green">A pagar {fmtL(total - ret)}</span>
          </div>
          <button type="submit" disabled={(saving) || gm.saving} data-testid="ge-submit" className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3 disabled:opacity-50">
            {saving ? "Guardando…" : "Registrar gasto"}
          </button>
        </form>
      </div>
    </div>
  );
}

const Fl = ({ l, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 block">{l}</label>
    {children}
  </div>
);
